/**
 * 在request基础上的业务封装，按照公司通用框架定义的数据格式进行封装。大部分情况下，直接使用default-request即可
 */
import Request from './request';
import type { AxiosResponse } from 'axios';
import type { ResponseData } from './types';
import Log from './log';
import { getLocalStore } from './local-store';

interface DefaultRequestOptions {
  // 展示提示消息
  showMessage: (msg: string, response?: AxiosResponse) => void;
  // 跳转至登录页面
  gotoLogin: () => void;
  /**
   * 是否返回包含 status 的返回结果，默认只返回其中的data数据
   */
  returnStatus?: boolean;
}

const defaultRequest = (options: DefaultRequestOptions) => {
  const request = new Request({
    baseURL: '',
    // 五分钟超时
    timeout: 1000 * 60 * 5,
    returnStatus: options.returnStatus,
    interceptors: {
      // 请求拦截器
      requestInterceptors: (config) => {
        // 添加请求头的业务参数
        const TENANT_ID = getLocalStore('tenantId');
        const isToken = (config.headers || {}).isToken === false;
        const token = getLocalStore('token');
        if (token && !isToken) {
          config.headers['Authorization'] = 'Bearer ' + token; // token
        }
        if (TENANT_ID) {
          config.headers['TENANT-ID'] = TENANT_ID; // 租户ID
        }
        return config;
      },
      // 响应拦截器
      responseInterceptors: (res: AxiosResponse<ResponseData<any>>) => {
        if (res.status === 401) {
          // 跳转登录
          options.showMessage(res.data.msg);
          options.gotoLogin();
          return;
        } else if (res.status !== 200) {
          // http错误
          const errorMsg = `网络请求异常，异常代码：${res.status}`;
          Log.error(errorMsg);
          options.showMessage(errorMsg);
          return Promise.reject(new Error(errorMsg));
        } else if (res.data.code === -1) {
          // 接口错误
          const errorMsg = res.data.msg || '接口请求异常';
          Log.error(errorMsg);
          options.showMessage(errorMsg);
          return Promise.reject(new Error(errorMsg));
        }
        return res;
      },
      // 响应拦截器 - 错误抛出
      responseInterceptorsError: (error) => {
        if (error.response.status === 401) {
          options.gotoLogin();
        }
        const msg = error?.response?.data?.msg || '接口异常！';
        console.error('error', error?.response?.data?.msg);
        options.showMessage(msg);
        return Promise.reject(error?.response);
      },
    },
  });
  return request;
};

export default defaultRequest;
